'use strict';

/**
 * T550 Private Vault — server-blind seal-box store crypto (Stage A, LD-1).
 *
 * The purest expression of CLAUDE.md's "custodian of ciphertext, not data":
 * the SERVER seals every item under the account's PUBLIC kex key and is
 * mathematically unable to decapsulate (read) what it sealed. Only the CLIENT,
 * holding the vault-resident PRIVATE kex key, can unseal. There is NO
 * JIT-decrypt-grant path (stronger than T548's master-key envelope).
 *
 * Composition (NOT a new primitive — GOTCHA-298):
 *   - metadata (filename, folder, tags, size) → sealed DIRECTLY as a small
 *     cbox-v1 record via seal-box `sealRecord` (no plaintext index, LD-1).
 *   - file body → encrypted with a fresh per-file DEK via AES-256-GCM
 *     (node:crypto), and the DEK itself sealed to the account pubkey as a small
 *     cbox-v1 record. The plaintext DEK is zeroized after use — the server
 *     cannot recover it (it is KEM-wrapped to a key the server can't open).
 *
 * Per-field vault-meta records (lock level, recovery config, storage mode,
 * "don't-show-again") each seal to their OWN cbox-v1 record carrying an INTERNAL
 * monotonic `version` INSIDE the sealed body (C2 — no 🟢 server-readable version
 * that would leak per-field mutation timing on a server-blind vault). Conflict
 * resolution is CLIENT-side: highest internal version wins, conflict surfaced.
 */

const crypto = require('crypto');
const { sealRecord, unsealRecord } = require('../../crypto/seal-box');

const BODY_ALGO = 'aes-256-gcm';
const DEK_LEN = 32; // AES-256

function toBuf(x) {
  if (Buffer.isBuffer(x)) return x;
  if (x instanceof Uint8Array) return Buffer.from(x);
  if (typeof x === 'string') return Buffer.from(x, 'utf8');
  throw new Error('strongbox: body must be Buffer/Uint8Array/string');
}

function zeroize(buf) {
  if (Buffer.isBuffer(buf) || buf instanceof Uint8Array) buf.fill(0);
}

/**
 * Encrypt a body under a fresh DEK (AES-256-GCM). Returns the envelope + the
 * DEK (caller seals then zeroizes the DEK).
 */
function encryptBody(bodyBytes) {
  const dek = crypto.randomBytes(DEK_LEN);
  const iv = crypto.randomBytes(12);
  const cipher = crypto.createCipheriv(BODY_ALGO, dek, iv);
  const ct = Buffer.concat([cipher.update(toBuf(bodyBytes)), cipher.final()]);
  const tag = cipher.getAuthTag();
  return {
    dek,
    body: { algo: BODY_ALGO, iv: iv.toString('hex'), ct: ct.toString('hex'), tag: tag.toString('hex') },
  };
}

function decryptBody(dek, body) {
  if (!body || body.algo !== BODY_ALGO) throw new Error('strongbox: unknown body algo');
  const decipher = crypto.createDecipheriv(BODY_ALGO, toBuf(dek), Buffer.from(body.iv, 'hex'));
  decipher.setAuthTag(Buffer.from(body.tag, 'hex'));
  return Buffer.concat([decipher.update(Buffer.from(body.ct, 'hex')), decipher.final()]);
}

/**
 * Seal a vault item (SERVER side — public key only).
 *
 * @param {object} args
 * @param {{x25519,mlKem}} args.pubKex   account PUBLIC kex key
 * @param {string} args.itemId
 * @param {{name,type,parentId,tags}} args.meta
 * @param {Buffer|Uint8Array|string} args.bodyBytes
 * @returns {object} sealed item record { id, meta(cbox-v1), dek(cbox-v1), body(aes-gcm), createdAt }
 */
function normalizeItemMeta(meta, size) {
  const m = meta || {};
  return {
    name: m.name || '',
    type: m.type || 'application/octet-stream',
    parentId: m.parentId != null ? m.parentId : null,
    tags: Array.isArray(m.tags) ? m.tags : [],
    size,
    createdAt: m.createdAt || Date.now(),
  };
}

function sealItem({ pubKex, itemId, meta, bodyBytes }) {
  if (!pubKex) throw new Error('strongbox: sealItem requires a recipient public kex key (fail-closed, LD-4)');
  if (!itemId) throw new Error('strongbox: sealItem requires itemId');
  const body = toBuf(bodyBytes == null ? Buffer.alloc(0) : bodyBytes);
  const enc = encryptBody(body);
  try {
    const metaPlain = normalizeItemMeta(meta, body.length);
    const metaEnv = sealRecord(metaPlain, pubKex, { seq: 0 });
    const dekEnv = sealRecord({ dek: enc.dek.toString('hex') }, pubKex, { seq: 0 });
    return { id: String(itemId), meta: metaEnv, dek: dekEnv, body: enc.body, createdAt: metaPlain.createdAt };
  } finally {
    zeroize(enc.dek);
  }
}

/**
 * Unseal a vault item (CLIENT side — requires the PRIVATE kex key). Fails
 * CLOSED on wrong key / tamper (throws).
 */
function unsealItem({ privKex, record }) {
  if (!record || !record.meta || !record.dek || !record.body) {
    throw new Error('strongbox: not a sealed item record');
  }
  const meta = unsealRecord(record.meta, privKex);
  const { dek } = unsealRecord(record.dek, privKex);
  const dekBuf = Buffer.from(dek, 'hex');
  try {
    const bodyBytes = decryptBody(dekBuf, record.body);
    return { id: record.id, meta, bodyBytes };
  } finally {
    zeroize(dekBuf);
  }
}

/**
 * Seal a single vault-meta field to its OWN cbox-v1 record. The `version` lives
 * INSIDE the sealed body (C2) so the server cannot observe per-field mutation
 * count/timing on a server-blind vault.
 */
function sealMetaField({ pubKex, field, value, version }) {
  if (!pubKex) throw new Error('strongbox: sealMetaField requires a recipient public kex key (fail-closed)');
  if (!field) throw new Error('strongbox: sealMetaField requires field');
  const v = Number.isInteger(version) ? version : 0;
  return sealRecord({ field, value, version: v }, pubKex, { seq: 0 });
}

function unsealMetaField({ privKex, sealed }) {
  const opened = unsealRecord(sealed, privKex);
  return { field: opened.field, value: opened.value, version: opened.version | 0 };
}

/**
 * Resolve a set of same-field sealed records (multi-device, set-unioned opaque
 * blobs). Highest internal version wins; a concurrent same-field edit is
 * SURFACED (never silently dropped — a security choice must not vanish). C2.
 */
function resolveMetaConflict({ privKex, sealedList }) {
  if (!Array.isArray(sealedList) || sealedList.length === 0) {
    throw new Error('strongbox: resolveMetaConflict requires a non-empty sealedList');
  }
  const opened = sealedList.map((s) => unsealMetaField({ privKex, sealed: s }));
  let winner = opened[0];
  for (const o of opened) {
    if (o.field !== winner.field) throw new Error('strongbox: resolveMetaConflict mixed fields');
    if (o.version > winner.version) winner = o;
  }
  // Conflict iff two DISTINCT values were seen at the winning-or-any version.
  const distinctValues = new Set(opened.map((o) => JSON.stringify(o.value)));
  return { field: winner.field, value: winner.value, version: winner.version, conflict: distinctValues.size > 1 };
}

module.exports = {
  BODY_ALGO,
  sealItem,
  unsealItem,
  sealMetaField,
  unsealMetaField,
  resolveMetaConflict,
  // exported for the fs/store + routes layers + tests
  _internals: { encryptBody, decryptBody, zeroize },
};
